import Vue from 'vue';
import axios from 'axios'; /* TODO: Setup and use Axios API instance */
import { endpoint, API_BASE_URL, AVAILABILITY_API_ENDPOINTS } from '@/utils/api/endpoints';
import { Availability, BookingRequest } from '@/models/availability.model';

export default {
	namespaced: true,

	state: {
		professionalAvailability: [],
		availability: [],
	},

	getters: {
		professionalAvailability: state => state.professionalAvailability.map(x => new Availability(x)),
		professionalUnavailabilty: (_, getters) => getters.professionalAvailability.filter(x => x.status === Availability.STATUS.UNAVAILABLE),
		professionalRequestsReceived: (_, getters) => {
			const requests = [];
			for (let availability of getters.professionalAvailability) {
				if (!availability.booking_requests) continue;

				for (let request of availability.booking_requests) {
					if (request.status === BookingRequest.STATUS.CANCELLED || request.status === BookingRequest.STATUS.REJECTED) continue;
					requests.push({ request, availability, availability_start_date: availability.date });
				}
			}
			return requests;
		},
	},

	mutations: {
		SET_PROFESSIONAL_AVAILABILITY: (state, data) => (state.professionalAvailability = data),
		SET_AVAILABILITY: (state, data) => (state.availability = data),
		UPDATE_AVAILABILITY: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.availability.findIndex(existing => existing.id === value.id && existing.availability_code === value.availability_code);
				if (index === -1) {
					Vue.set(state.availability, state.availability.length, value);
				} else {
					Vue.set(state.availability, index, value);
				}
			}
		},
		UPDATE_PROFESSIONAL_AVAILABILITY: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.professionalAvailability.findIndex(existing => existing.id === value.id && existing.availability_code === value.availability_code);
				if (index === -1) {
					Vue.set(state.professionalAvailability, state.professionalAvailability.length, value);
				} else {
					Vue.set(state.professionalAvailability, index, value);
				}
			}
		},
		REMOVE_PROFESSIONAL_AVAILABILITY: (state, data) => {
			if (!Array.isArray(data)) data = [data];
			for (let value of data) {
				const index = state.professionalAvailability.findIndex(existing => existing.id === value.id && existing.availability_code === value.availability_code);
				if (index !== -1) {
					Vue.delete(state.professionalAvailability, index);
				}
			}
		},
	},

	actions: {
		async _LoadAvailability({ rootGetters }, { endPoint, params, query }) {
			if (!endPoint) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const url = endpoint(API_BASE_URL, endPoint, { params, query });
				const results = await axios.get(url, { headers: { Authorization: rootGetters.jwtToken } });
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadProfessionalAvailability({ commit, dispatch }, { startDate, endDate, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (typeof startDate !== 'string' || typeof endDate !== 'string') return;
			try {
				const results = await dispatch('_LoadAvailability', {
					endPoint: AVAILABILITY_API_ENDPOINTS.BY_DATE,
					query,
					params: {
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				commit('UPDATE_PROFESSIONAL_AVAILABILITY', results.data.Items);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async FetchProfessionalAvailability({ dispatch }, { startDate, endDate, availabilityType, query = null }) {
			if (!startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const results = await dispatch('_LoadAvailability', {
					endPoint: AVAILABILITY_API_ENDPOINTS.BY_DATE,
					query,
					params: {
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				const availabilityResults = results.data.Items;

				let requests = [];
				if (availabilityType === 'availability-requests') {
					for (let availability of availabilityResults) {
						if (!availability.booking_requests) continue;

						for (let request of availability.booking_requests) {
							if (request.status === BookingRequest.STATUS.CANCELLED || request.status === BookingRequest.STATUS.REJECTED) continue;
							requests.push({ request, availability, availability_start_date: availability.date });
						}
					}
				}

				if (availabilityType === 'availabilities') {
					requests = availabilityResults.filter(x => x.status !== Availability.STATUS.UNAVAILABLE);
				}

				if (availabilityType === 'unavailabilities') {
					requests = availabilityResults.filter(x => x.status === Availability.STATUS.UNAVAILABLE);
				}

				return requests;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadAvailabilityByProfessional(_, { companyId, professionalId, start_date, end_date, query = null }) {
			if (!professionalId) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const params = {
					companyId,
					professionalId,
					start_date,
					end_date,
				};
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.BY_PROFESSIONAL, { params, query });
				const results = await axios.get(url);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async LoadAvailabilityByProfessionalForCompany(_, { companyId, professionalId, start_date, end_date, query = null }) {
			if (!professionalId || !companyId) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const params = {
					companyId,
					professionalId,
					start_date,
					end_date,
				};
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.BY_PROFESSIONAL_FOR_COMPANY, { params, query });
				const results = await axios.get(url);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				return results.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async SearchAvailability({ commit, dispatch }, { profession, startDate, endDate, query = null }) {
			if (!profession || !startDate || !endDate) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const results = await dispatch('_LoadAvailability', {
					endPoint: AVAILABILITY_API_ENDPOINTS.SEARCH,
					query,
					params: {
						profession,
						startDate,
						endDate,
					},
				});

				if (!results) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to load availability' });

				commit('SET_AVAILABILITY', results);
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async CreateAvailability(_, availability) {
			if (!availability) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(availability) && typeof availability !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Availability can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.CREATE);
				const results = await axios.post(url, availability);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to create or load availability' });

				return results.data.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async DeleteAvailability(_, { id, date }) {
			if (!id || !date) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const params = { id, date };
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.DELETE, { params });
				const results = await axios.delete(url, params);

				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to delete availability' });

				return results.data.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpdateAvailability(_, availability) {
			if (!availability) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });
			if (Array.isArray(availability) && typeof availability !== 'object') throw window.$Error(window.$ERROR.ERR_TYPE, { parent: 'Availability can only be an object' });

			try {
				const url = endpoint(API_BASE_URL, AVAILABILITY_API_ENDPOINTS.UPDATE);
				const results = await axios.put(url, availability);
				if (!results.data) throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: 'Failed to update or load availability' });

				return results.data.data;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},

		async UpsertAvailability({ dispatch, commit }, availability) {
			if (!availability) throw window.$Error(window.$ERROR.ERR_MISSING_ARG, { parent: 'Argument not provided' });

			try {
				const result = await dispatch(availability.id ? 'UpdateAvailability' : 'CreateAvailability', availability);
				commit('UPDATE_PROFESSIONAL_AVAILABILITY', result);
				return result;
			} catch (err) {
				throw window.$Error(window.$ERROR.ERR_REQUEST_FAILED, { parent: err });
			}
		},
	},
};
